import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Linkedin, Mail, Send } from "lucide-react";
import { CONTACT } from "../../data/content";
import { Reveal, SectionHeading } from "./Reveal";
import { useSite } from "../../context/SiteContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Name", type: "text", required: true, placeholder: "Dr. Jane Smith" },
  { name: "practice_name", label: "Practice Name", type: "text", placeholder: "Lakeside Family Medicine" },
  { name: "email", label: "Email", type: "email", required: true, placeholder: "jane@practice.com" },
  { name: "phone", label: "Phone", type: "tel", placeholder: "(555) 012-3456" },
  { name: "specialty", label: "Specialty", type: "text", placeholder: "Family Medicine" },
];

export const inputClass =
  "w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-sky/60 focus:border-brand-sky transition-[border-color,box-shadow] duration-300";

const Contact = () => {
  const { openBooking } = useSite();
  const [form, setForm] = useState({ name: "", practice_name: "", email: "", phone: "", specialty: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry sent — we'll be in touch within one business day.");
      setForm({ name: "", practice_name: "", email: "", phone: "", specialty: "", message: "" });
    } catch {
      toast.error("Something went wrong sending your enquiry. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-white dark:bg-brand-ink py-24 md:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[0.85fr_1.15fr] gap-16">
        <div>
          <SectionHeading
            id="contact"
            overline="Contact Us"
            title={<>Let's talk about <span className="text-brand-gradient">your practice</span></>}
            sub="Tell us about your specialty, your EHR, and your documentation load — we'll show you exactly what live human scribing looks like in your workflow."
          />
          <Reveal delay={0.2}>
            <div className="mt-9 space-y-4">
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 rounded-2xl border border-slate-100 dark:border-white/10 p-5 hover:border-brand-sky transition-colors duration-300 group"
              >
                <div className="w-11 h-11 rounded-xl bg-brand-sky-soft dark:bg-white/10 flex items-center justify-center">
                  <Mail size={18} className="text-brand-navy dark:text-brand-sky" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Office Email</p>
                  <p className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-brand-navy dark:group-hover:text-brand-sky transition-colors duration-300">{CONTACT.email}</p>
                </div>
              </a>
              <a
                href={CONTACT.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-linkedin-link"
                className="flex items-center gap-4 rounded-2xl border border-slate-100 dark:border-white/10 p-5 hover:border-brand-sky transition-colors duration-300 group"
              >
                <div className="w-11 h-11 rounded-xl bg-brand-sky-soft dark:bg-white/10 flex items-center justify-center">
                  <Linkedin size={18} className="text-brand-navy dark:text-brand-sky" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">LinkedIn</p>
                  <p className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-brand-navy dark:group-hover:text-brand-sky transition-colors duration-300">Medlyn 360</p>
                </div>
              </a>
              <button
                data-testid="contact-book-call-button"
                onClick={openBooking}
                className="w-full rounded-2xl bg-brand-navy dark:bg-brand-sky text-white dark:text-brand-ink font-bold px-6 py-4 text-sm hover:shadow-[0_16px_40px_-14px_rgba(42,49,146,0.55)] transition-shadow duration-300"
              >
                Book a Discovery Call
              </button>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-[2rem] bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 p-8 md:p-10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.name === "specialty" ? "sm:col-span-2" : ""}>
                  <label htmlFor={`contact-${f.name}`} className="block text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">
                    {f.label}{f.required && <span className="text-brand-sky"> *</span>}
                  </label>
                  <input
                    id={`contact-${f.name}`}
                    data-testid={`contact-input-${f.name.replace(/_/g, "-")}`}
                    type={f.type}
                    required={f.required}
                    placeholder={f.placeholder}
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className={inputClass}
                  />
                </div>
              ))}
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">Message</label>
              <textarea
                id="contact-message"
                data-testid="contact-input-message"
                rows={5}
                placeholder="Tell us about your practice, patient volume, and current documentation workflow…"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`${inputClass} resize-none`}
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="group w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-brand-navy dark:bg-brand-sky text-white dark:text-brand-ink font-bold px-8 py-4 text-sm hover:shadow-[0_20px_45px_-14px_rgba(42,49,146,0.55)] disabled:opacity-60 transition-shadow duration-300"
            >
              {sending ? "Sending…" : "Send Enquiry"}
              <Send size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <p className="text-[11px] text-center text-slate-400">HIPAA-conscious by design — please don't include patient information in this form.</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
export default Contact;
